import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, BadgeCheck, Briefcase, Clock, ArrowLeft } from 'lucide-react';
import api, { fileUrl } from './api';
import { useAuth } from './AuthContext';
import { StarsDisplay } from './Stars';

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return ''; } };

const ProfessionalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [op, setOp] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/operatives/${id}`).then((r) => setOp(r.data)).catch(() => setErr('This professional profile is not available.'));
    api.get(`/operatives/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id]);

  if (err) return <div className="py-24 text-center text-gray-600" data-testid="pro-detail-error">{err} <div className="mt-4"><Link to="/find-a-professional" className="text-[#3a9b5c] hover:underline">← Back to search</Link></div></div>;
  if (!op) return <div className="py-24 text-center text-gray-500">Loading…</div>;

  return (
    <div className="bg-[#e9f3ec] py-12 min-h-[70vh]" data-testid="professional-detail-page">
      <div className="mx-auto max-w-3xl px-5">
        <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1b4f72]"><ArrowLeft size={16} /> Back</button>
        <div className="rounded-2xl bg-white p-7 shadow-sm">
          <div className="flex items-center gap-5">
            <div className="h-24 w-24 shrink-0 overflow-hidden rounded-full bg-[#e9f3ec] ring-2 ring-[#3a9b5c]/25">
              {op.photoFileId ? <img src={fileUrl(op.photoFileId)} alt={op.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-3xl font-bold text-[#3a9b5c]">{(op.name || '?').charAt(0)}</div>}
            </div>
            <div>
              <h1 className="flex items-center gap-2 ea-heading text-2xl font-bold">{op.name} <BadgeCheck size={20} className="text-[#3a9b5c]" /></h1>
              {op.companyName && <p className="text-gray-500">{op.companyName}</p>}
              <div className="mt-1 flex items-center gap-2">
                <StarsDisplay value={op.rating || 0} />
                <span className="text-sm text-gray-500" data-testid="pro-rating-summary">{op.rating ? `${op.rating.toFixed(1)} (${op.reviewCount} review${op.reviewCount === 1 ? '' : 's'})` : 'No reviews yet'}</span>
              </div>
              <span className="mt-1 inline-block rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-700">Verified Professional</span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">{op.categories.map((c) => <span key={c} className="rounded-full bg-[#e9f3ec] px-3 py-1 text-sm font-medium text-[#3a9b5c]">{c}</span>)}</div>

          {op.bio && <p className="mt-5 whitespace-pre-line break-words text-sm leading-relaxed text-gray-700">{op.bio}</p>}

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {op.yearsExperience && <div className="flex items-center gap-2 text-sm text-gray-600"><Briefcase size={16} className="text-[#3a9b5c]" /> {op.yearsExperience} years experience</div>}
            {op.hourlyRate != null && <div className="flex items-center gap-2 text-sm text-gray-600"><Clock size={16} className="text-[#3a9b5c]" /> From £{op.hourlyRate}/hr</div>}
            {op.serviceAreas.length > 0 && <div className="flex items-start gap-2 text-sm text-gray-600 sm:col-span-2"><MapPin size={16} className="mt-0.5 shrink-0 text-[#3a9b5c]" /> {op.serviceAreas.join(', ')}</div>}
          </div>

          {op.skills.length > 0 && (
            <div className="mt-6"><p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Skills</p><div className="flex flex-wrap gap-2">{op.skills.map((s) => <span key={s} className="rounded-lg bg-gray-50 px-2.5 py-1 text-xs text-gray-600">{s}</span>)}</div></div>
          )}

          <div className="mt-7 rounded-xl bg-[#e9f3ec] p-5 text-center">
            <p className="text-sm text-gray-600">Want to hire {op.name.split(' ')[0]}? Post your job and invite verified professionals to bid.</p>
            <Link data-testid="pro-detail-post-job" to={user && user.role === 'client' ? '/post-a-job' : '/auth?role=client&next=/post-a-job'} className="mt-3 inline-block rounded-full bg-[#3a9b5c] px-7 py-2.5 text-sm font-semibold text-white hover:bg-[#318150]">Post a Job</Link>
          </div>
        </div>

        <div className="mt-6 rounded-2xl bg-white p-7 shadow-sm" data-testid="reviews-section">
          <h2 className="font-display text-xl font-bold text-[#1b4f72]">Reviews {reviews.length > 0 ? `(${reviews.length})` : ''}</h2>
          {reviews.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">No reviews yet — this professional is newly verified.</p>
          ) : (
            <div className="mt-4 space-y-4">
              {reviews.map((r) => (
                <div key={r.id} className="rounded-xl border border-gray-100 p-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><StarsDisplay value={r.rating} size={14} /><span className="text-sm font-semibold text-[#1b4f72]">{r.clientName}</span></div>
                    <span className="text-xs text-gray-400">{fmtDate(r.createdAt)}</span>
                  </div>
                  {r.comment && <p className="mt-2 break-words text-sm text-gray-700">{r.comment}</p>}
                  {r.response && <div className="mt-3 rounded-lg bg-[#f4f8f5] p-3"><p className="text-xs font-semibold text-[#3a9b5c]">Response from {op.name}</p><p className="mt-1 break-words text-sm text-gray-700">{r.response}</p></div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalDetail;
